import { enhancedApi } from '@store/generated/patient-requirement';

/**
 * Feature-specific tag types for patient requirements.
 *
 * Definitions are org-scoped, so the list tag carries the org id: two orgs
 * open in two tabs must not invalidate each other's catalog.
 */
const PATIENT_REQUIREMENT_TAG_TYPES = [
  'RequirementDefinition',
  'RequirementDefinitionsList',
  'PatientRequirements',
  'MissingRequirements',
  'RequirementDeliveryHistory',
  'RequestCopy',
] as const;

export const patientRequirementsApi = enhancedApi.enhanceEndpoints({
  addTagTypes: PATIENT_REQUIREMENT_TAG_TYPES,
  endpoints: {
    getV2ProviderOrganizationsByOrgIdPatientRequirementDefinitions: {
      providesTags: (result, _error, { orgId }) =>
        result
          ? [
              ...result.items.map(({ patient_requirement_definition_id }) => ({
                type: 'RequirementDefinition' as const,
                id: patient_requirement_definition_id,
              })),
              { type: 'RequirementDefinitionsList' as const, id: `ORG-${orgId}` },
            ]
          : [{ type: 'RequirementDefinitionsList' as const, id: `ORG-${orgId}` }],
    },

    getV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionId: {
      providesTags: (_result, _error, { definitionId }) => [
        { type: 'RequirementDefinition' as const, id: definitionId },
      ],
    },

    postV2ProviderOrganizationsByOrgIdPatientRequirementDefinitions: {
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: 'RequirementDefinitionsList' as const, id: `ORG-${orgId}` },
      ],
    },

    patchV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionId: {
      invalidatesTags: (_result, _error, { orgId, definitionId }) => [
        { type: 'RequirementDefinition' as const, id: definitionId },
        { type: 'RequirementDefinitionsList' as const, id: `ORG-${orgId}` },
        // Deactivating a definition cancels its rows, and editing the label
        // changes what staff read on the patient record.
        { type: 'PatientRequirements' as const, id: 'LIST' },
        { type: 'MissingRequirements' as const, id: `DEF-${definitionId}` },
      ],
    },

    // Settings-owned default request copy (GDN-3230/GDN-3450, AC-11).
    getV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopy: {
      providesTags: (_result, _error, { orgId }) => [
        { type: 'RequestCopy' as const, id: `ORG-${orgId}` },
      ],
    },

    // The preview composes the SAVED copy — it takes only an org id — so it is
    // tagged with the copy itself and refetches when a save invalidates it.
    getV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopyPreview: {
      providesTags: (_result, _error, { orgId }) => [
        { type: 'RequestCopy' as const, id: `ORG-${orgId}` },
      ],
    },

    putV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopy: {
      // Both the copy and its composed preview are stale after a save. On
      // failure `result` is undefined and nothing is invalidated: a refused
      // save must not blank the editor's own view of what is stored.
      invalidatesTags: (result, _error, { orgId }) =>
        result ? [{ type: 'RequestCopy' as const, id: `ORG-${orgId}` }] : [],
    },

    // Staff surfaces (GDN-3226).
    getV2ProviderOrganizationsByOrgIdPatientRequirementsMissing: {
      providesTags: (_result, _error, { patientRequirementDefinitionId }) => [
        {
          type: 'MissingRequirements' as const,
          id: `DEF-${patientRequirementDefinitionId}`,
        },
      ],
    },

    getV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirements: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'PatientRequirements' as const, id: patientId },
        { type: 'PatientRequirements' as const, id: 'LIST' },
      ],
    },

    getV2ProviderOrganizationsByOrgIdPatientsAndPatientIdRequirementRequests: {
      providesTags: (_result, _error, { patientId }) => [
        { type: 'RequirementDeliveryHistory' as const, id: patientId },
      ],
    },

    postV2ProviderOrganizationsByOrgIdPatientsAndPatientIdRequirementRequests: {
      // Sending changes no requirement state — only canonical evidence does
      // that. It does change the derived "Requested" display and flips the
      // action to Remind, both of which read from the delivery history.
      invalidatesTags: (_result, _error, { patientId }) => [
        { type: 'RequirementDeliveryHistory' as const, id: patientId },
        { type: 'PatientRequirements' as const, id: patientId },
      ],
    },

    // AC-8 writes. Both keyed off the RESULT's definition id: the args carry
    // only the requirement id, and the missing list is tagged per definition.
    // On failure `result` is undefined and nothing is invalidated, which is
    // what we want — a rejected waive must not blank the list it didn't change.
    postV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirementsRequirementIdWaive:
      {
        invalidatesTags: (result, _error, { patientId }) =>
          result
            ? [
                { type: 'PatientRequirements' as const, id: patientId },
                {
                  type: 'MissingRequirements' as const,
                  id: `DEF-${result.patient_requirement_definition_id}`,
                },
              ]
            : [],
      },

    postV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirementsRequirementIdReopen:
      {
        invalidatesTags: (result, _error, { patientId }) =>
          result
            ? [
                { type: 'PatientRequirements' as const, id: patientId },
                {
                  type: 'MissingRequirements' as const,
                  id: `DEF-${result.patient_requirement_definition_id}`,
                },
              ]
            : [],
      },
  },
});

// User-friendly hook names only — never the generated OpenAPI names.
export const {
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdBatchesPreviewQuery:
    useGetBatchPreviewQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdBatchesQuery:
    useGetBatchesQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdBatchesBatchIdQuery:
    useGetBatchQuery,
  usePostV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdBatchesMutation:
    useLaunchBatchMutation,
  usePostV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdBatchesBatchIdCancelMutation:
    useCancelBatchMutation,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsQuery:
    useGetRequirementDefinitionsQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdQuery:
    useGetRequirementDefinitionQuery,
  usePostV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsMutation:
    useCreateRequirementDefinitionMutation,
  usePatchV2ProviderOrganizationsByOrgIdPatientRequirementDefinitionsAndDefinitionIdMutation:
    useUpdateRequirementDefinitionMutation,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementsMissingQuery:
    useGetMissingRequirementsQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirementsQuery:
    useGetPatientRequirementsQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientsAndPatientIdRequirementRequestsQuery:
    useGetRequirementDeliveryHistoryQuery,
  usePostV2ProviderOrganizationsByOrgIdPatientsAndPatientIdRequirementRequestsMutation:
    useSendRequirementRequestMutation,
  usePostV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirementsRequirementIdWaiveMutation:
    useWaiveRequirementMutation,
  usePostV2ProviderOrganizationsByOrgIdPatientsAndPatientIdPatientRequirementsRequirementIdReopenMutation:
    useReopenRequirementMutation,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopyQuery:
    useGetRequestCopyQuery,
  useGetV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopyPreviewQuery:
    useGetRequestCopyPreviewQuery,
  usePutV2ProviderOrganizationsByOrgIdPatientRequirementRequestCopyMutation:
    useSaveRequestCopyMutation,
} = patientRequirementsApi;
